import type { StudentUpdateInput } from "@/types/student"
import { memo, useEffect, useId, useMemo } from "react"

import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import type { Resolver } from "react-hook-form"

import {
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Portal,
  Switch,
  Textarea,
  VStack,
} from "@chakra-ui/react"

import { Field } from "@/components/atoms/Field"
import ForeignKeySelect from "@/components/molecules/ForeignKeySelect"
import { useMyHousehold } from "@/hooks/api/useMyHousehold"
import { useSchoolSettings } from "@/hooks/api/useSchoolSettings"

const skipLinkStyle = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  background: "#3182ce",
  color: "white",
  padding: "8px 16px",
  textDecoration: "none",
  borderRadius: "6px",
  zIndex: 1000,
} as const

type StudentFormValues = StudentUpdateInput

interface StudentFormDialogProps {
  isOpen: boolean
  title: string
  submitLabel: string
  resolver: Resolver<StudentFormValues>
  defaultValues: StudentFormValues
  onSubmit: (values: StudentFormValues) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  cancelLabel?: string
  finalFocusRef?: React.RefObject<HTMLElement | null>
  showSkipLink?: boolean
  loadingText?: string
}

const StudentFormDialog = memo(
  ({
    isOpen,
    title,
    submitLabel,
    resolver,
    defaultValues,
    onSubmit,
    onClose,
    isSubmitting = false,
    cancelLabel = "Cancel",
    finalFocusRef,
    showSkipLink = false,
    loadingText,
  }: StudentFormDialogProps) => {
    const formSectionId = useId()

    // Get current user's household (uses /households/me endpoint)
    const { data: myHousehold, isLoading: householdLoading } = useMyHousehold()
    const { swimLevels, locations } = useSchoolSettings()

    console.log("🏠 StudentFormDialog - My Household:", {
      myHousehold,
      isLoading: householdLoading,
      householdId: myHousehold?.id,
    })

    const normalizedDefaults = useMemo<StudentFormValues>(
      () => ({
        first_name: defaultValues.first_name ?? "",
        last_name: defaultValues.last_name ?? "",
        date_of_birth: defaultValues.date_of_birth ?? "",
        gender: defaultValues.gender ?? null,
        // Auto-select household if only one available and no existing value
        household_id: defaultValues.household_id ?? (myHousehold?.id || ""),
        current_swim_level: defaultValues.current_swim_level ?? null,
        skill_level_notes: defaultValues.skill_level_notes ?? null,
        has_asthma: defaultValues.has_asthma ?? false,
        // Auto-select preferred location if no existing value (only Mountain View available)
        preferred_location: defaultValues.preferred_location ?? "Mountain View",
        notes: defaultValues.notes ?? null,
        is_active: defaultValues.is_active ?? false,
        photo_url: defaultValues.photo_url ?? null,
      }),
      [defaultValues, myHousehold],
    )

    const form = useForm<StudentFormValues>({
      resolver,
      mode: "onBlur",
      defaultValues: normalizedDefaults,
    })

    useEffect(() => {
      if (isOpen) {
        void form.reset(normalizedDefaults)
      }
    }, [form, isOpen, normalizedDefaults])

    // Auto-select household when it loads (if not already set)
    useEffect(() => {
      if (myHousehold && isOpen && !form.getValues("household_id")) {
        form.setValue("household_id", myHousehold.id)
      }
    }, [myHousehold, isOpen, form])

    const handleClose = () => {
      form.reset(normalizedDefaults)
      onClose()
    }

    const handleSubmit: SubmitHandler<StudentFormValues> = async (values) => {
      try {
        await onSubmit(values)
        handleClose()
      } catch (error) {
        // Error handling is delegated to the mutation layer
      }
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            handleClose()
          }
        }}
        size={{ base: "full", md: "md" }}
        trapFocus
        restoreFocus
        initialFocusEl={() => null}
        finalFocusEl={() => finalFocusRef?.current ?? null}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent as="form" onSubmit={form.handleSubmit(handleSubmit)} maxH="90vh" display="flex" flexDirection="column">
              {showSkipLink && (
                <a
                  href={`#${formSectionId}`}
                  style={skipLinkStyle}
                  onFocus={(event) => {
                    event.currentTarget.style.left = "16px"
                    event.currentTarget.style.top = "16px"
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.left = "-9999px"
                    event.currentTarget.style.top = "-9999px"
                  }}
                >
                  Skip to form fields
                </a>
              )}

              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody id={formSectionId} overflowY="auto" flex="1">
                <VStack gap={6}>
                  <Field
                    required
                    invalid={!!form.formState.errors.first_name}
                    errorText={form.formState.errors.first_name?.message}
                    label="First Name"
                  >
                    <Input
                      placeholder={"Enter first name"}
                      aria-invalid={!!form.formState.errors.first_name}
                      {...form.register("first_name")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.last_name}
                    errorText={form.formState.errors.last_name?.message}
                    label="Last Name"
                  >
                    <Input
                      placeholder={"Enter last name"}
                      aria-invalid={!!form.formState.errors.last_name}
                      {...form.register("last_name")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.date_of_birth}
                    errorText={form.formState.errors.date_of_birth?.message}
                    label="Date Of Birth"
                  >
                    <Input
                      type="date"
                      placeholder={"Select date of birth"}
                      aria-invalid={!!form.formState.errors.date_of_birth}
                      {...form.register("date_of_birth")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.gender}
                    errorText={form.formState.errors.gender?.message}
                    label="Gender"
                  >
                    <select
                      {...form.register("gender", {
                        setValueAs: (value) => (value === "" ? null : value),
                      })}
                      aria-invalid={!!form.formState.errors.gender}
                      className="chakra-select"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer_not_to_say">
                        Prefer Not To Say
                      </option>
                    </select>
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.household_id}
                    errorText={form.formState.errors.household_id?.message}
                    label="Household"
                  >
                    <ForeignKeySelect
                      register={form.register("household_id")}
                      options={myHousehold ? [myHousehold] : []}
                      isLoading={householdLoading}
                      placeholder="Select Household"
                      hasError={!!form.formState.errors.household_id}
                      formatLabel={(h) =>
                        `${h.parent_first_name} ${h.parent_last_name} - ${h.email}`
                      }
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.current_swim_level}
                    errorText={
                      form.formState.errors.current_swim_level?.message
                    }
                    label="Current Swim Level"
                  >
                    <select
                      {...form.register("current_swim_level", {
                        setValueAs: (value) => (value === "" ? null : value),
                      })}
                      aria-invalid={!!form.formState.errors.current_swim_level}
                      className="chakra-select"
                    >
                      <option value="">Select Current Swim Level</option>
                      {swimLevels.map((sl) => (
                        <option key={sl.value} value={sl.value}>{sl.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.skill_level_notes}
                    errorText={form.formState.errors.skill_level_notes?.message}
                    label="Skill Level Notes"
                  >
                    <Textarea
                      {...form.register("skill_level_notes")}
                      placeholder={"Enter skill level notes"}
                      rows={3}
                      resize="vertical"
                      aria-invalid={!!form.formState.errors.skill_level_notes}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.has_asthma}
                    errorText={form.formState.errors.has_asthma?.message}
                    label="Has Asthma"
                  >
                    <Controller
                      name="has_asthma"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="has_asthma"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Has Asthma"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.preferred_location}
                    errorText={
                      form.formState.errors.preferred_location?.message
                    }
                    label="Preferred Location"
                  >
                    <select
                      {...form.register("preferred_location", {
                        setValueAs: (value) => (value === "" ? null : value),
                      })}
                      aria-invalid={!!form.formState.errors.preferred_location}
                      className="chakra-select"
                    >
                      <option value="">Select Preferred Location</option>
                      {locations.map((loc) => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.notes}
                    errorText={form.formState.errors.notes?.message}
                    label="Notes"
                  >
                    <Textarea
                      {...form.register("notes")}
                      placeholder={"Enter notes"}
                      rows={3}
                      resize="vertical"
                      aria-invalid={!!form.formState.errors.notes}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.is_active}
                    errorText={form.formState.errors.is_active?.message}
                    label="Is Active"
                  >
                    <Controller
                      name="is_active"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="is_active"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Is Active"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.photo_url}
                    errorText={form.formState.errors.photo_url?.message}
                    label="Photo Url"
                  >
                    <Input
                      placeholder={"Enter photo url"}
                      aria-invalid={!!form.formState.errors.photo_url}
                      {...form.register("photo_url")}
                    />
                  </Field>
                </VStack>
              </DialogBody>

              <DialogFooter>
                <HStack gap={3}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    loading={isSubmitting}
                    loadingText={loadingText ?? `${submitLabel}...`}
                  >
                    {submitLabel}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

StudentFormDialog.displayName = "StudentFormDialog"

export default StudentFormDialog
